import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { cookieManager } from '../../utils/cookieManager.js';
import AssistantInputPanel from './AssistantInputPanel';
import AssistantOutputPanel from './AssistantOutputPanel';
import {
  getConsularDistrict,
  canWaiveInterview,
  needsEVUSCheck,
  generateMaterialList,
  generateProcessSteps
} from './visaLogic';

/**
 * B1/B2签证材料与流程助手 - 主容器组件
 *
 * 功能：
 * 1. 认证检查 - 需要登录才能使用
 * 2. 状态管理 - 管理用户输入和实时计算结果
 * 3. 实时计算 - 使用 useMemo 优化性能
 * 4. 布局管理 - 左侧输入、右侧输出的分栏布局
 */
const VisaAssistantSection = () => {
  const navigate = useNavigate();
  const authInfo = cookieManager.getAuthInfo();
  const isLoggedIn = authInfo.isLoggedIn;

  // 用户输入数据
  const [userInputs, setUserInputs] = useState({
    purpose: '', // 旅游/探亲/商务
    nationality: 'CN', // 国籍（默认中国）
    age: '', // 年龄
    province: '', // 常住地省份（使用 provinceData 的 key）
    visaStatus: 'need_apply', // 🆕 签证申请状态：'need_apply' 需要申请 | 'have_visa' 已有签证
    isFirstTime: true, // 是否首次申请
    lastVisaType: '', // 上次签证类型
    lastVisaFullValidity: null, // 上次是否全有效期
    lastVisaExpiredMonths: '', // 上次到期月数
    ageWhenLastIssued: '', // ⚠️ 关键字段：上次签发时年龄
    applyInResidenceCountry: null, // 是否在居住国申请
    everRefused: false, // 是否被拒签过
    has10YearVisa: false // 是否持有10年B类签证
  });

  // 实时计算结果（使用 useMemo 优化性能，避免不必要的重新渲染）
  const computedResults = useMemo(() => {
    // 判断是否有足够的输入信息
    const hasMinimalInput = userInputs.province && userInputs.age;

    if (!hasMinimalInput) {
      return {
        consularDistrict: '',
        canWaiveInterview: false,
        needsEVUS: false,
        materialGroups: [],
        processSteps: []
      };
    }

    // 计算是否符合免面谈条件
    const canWaive = canWaiveInterview(userInputs);
    const needsEVUS = needsEVUSCheck(userInputs);

    // 构建完整的profile对象，用于生成材料清单和流程步骤
    const profile = {
      ...userInputs,
      canWaiveInterview: canWaive,
      needsEVUSCheck: needsEVUS
    };

    // 🆕 已有签证场景：仅显示EVUS相关材料
    let materialGroups = [];
    if (userInputs.visaStatus === 'have_visa') {
      // 已有签证场景：仅显示EVUS提醒
      materialGroups = needsEVUS ? [{
        id: 'G5',
        title: 'EVUS登记提醒 (EVUS Alert)',
        subtitle: '10年B类签证持有人必须完成',
        color: 'red',
        items: [
          {
            text: '🚨 您持有10年B1/B2签证，出发前必须完成EVUS登记，否则可能无法登机',
            highlight: true
          },
          {
            text: 'EVUS有效期通常为2年，护照或签证变更后需重新登记',
            highlight: false
          },
          { text: 'EVUS登记网址：evus.gov（英文）', highlight: false }
        ]
      }] : [];
    } else {
      // 原有逻辑：需要申请签证的场景
      materialGroups = generateMaterialList(profile);
    }

    return {
      consularDistrict: getConsularDistrict(userInputs.province),
      canWaiveInterview: canWaive,
      needsEVUS: needsEVUS,
      materialGroups: materialGroups,
      processSteps: generateProcessSteps(userInputs)
    };
  }, [userInputs]);

  // 处理导航
  const handleNavigate = (path) => {
    navigate(path);
  };

  return (
    <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4">
        {/* 标题区域 */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-gray-900 to-blue-800 bg-clip-text text-transparent mb-3">
            B1/B2签证材料与流程助手
          </h2>
          <p className="text-gray-600 text-lg">
            填写信息，实时获取您的专属材料清单和办理流程
          </p>
          <p className="text-gray-500 text-sm mt-2">
            需要帮助了解是否符合免面谈条件？需要EVUS登记提醒？请先登录！
          </p>
        </div>

        {/* 未登录引导界面 */}
        {!isLoggedIn ? (
          <div className="max-w-2xl mx-auto bg-white rounded-2xl border-2 border-blue-200 p-8 shadow-lg text-center">
            <Shield className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              登录后使用签证助手
            </h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              为了为您提供个性化的签证建议和材料清单，请先登录您的账户。本助手将根据您的具体情况实时生成所需材料和办理流程。
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => handleNavigate('/auth/login')}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl"
              >
                立即登录
              </button>
              <button
                onClick={() => handleNavigate('/auth/register')}
                className="px-6 py-3 bg-white text-blue-600 border-2 border-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-all"
              >
                注册账户
              </button>
            </div>
          </div>
        ) : (
          /* 已登录：左右分栏布局 */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* 左侧：输入面板 */}
            <div>
              <AssistantInputPanel
                userInputs={userInputs}
                setUserInputs={setUserInputs}
                computedResults={computedResults}
              />
            </div>

            {/* 右侧：输出面板 */}
            <div>
              <AssistantOutputPanel
                computedResults={computedResults}
                userInputs={userInputs}
                onNavigate={handleNavigate}
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default VisaAssistantSection;
